import React, { useEffect, useState, useRef, useMemo } from "react";
import styles from "./assetCard.module.scss";
import { Media } from "../../../../types/media";
import cx from "classnames";
import Thumbnail from "../../../../components/thumbnail";
import dateFormat from "dateformat";
import Badge from "src/components/badge";
import { FileService } from "src/services/file.service";
import VideoService from "src/services/video.service";
import videojs from "video.js";
import _ from "lodash";
import Tooltip from "src/components/tooltip/index";
import { useNavigate } from "react-router-dom";
import { NavigateEntity } from "src/components/NavigateEntity";
import Text from "src/components/text";

const renderThumbnail = (icon: Media) => {
  return (
    <Thumbnail
      media={icon}
      width={400}
      height={205}
      className={styles.thumbnail}
      alt="asset thumbnail"
      defaultImage="/images/assets/default_asset.jpg"
    />
  );
};

interface AssetIconProps {
  progress: number;
  item: any;
}

export function AssetCard({
  progress,
  item
}: AssetIconProps) {
  const navigate = useNavigate();

  const [video, setVideo] = useState<{ src: any; type: any } | null>(null);
  const videoNode = useRef<HTMLVideoElement | null>(null);
  const playerRef = useRef<any>(null);

  const internal = item.settings && !item.settings.share 

  useEffect(() => {
    setVideo(getVideoMetadata());
  }, [])

  useEffect(() => {
    if (video) {
      loadPlayer();
    }
    return () => {
      if (playerRef.current) {
        playerRef.current.dispose();
        playerRef.current = null;
      }
    };
  }, [video])

  const loadPlayer = () => {
    if (!videoNode.current || !video) {
      return;
    }

    if (!playerRef.current) {
      playerRef.current = videojs(videoNode.current, video, () => {
        playerRef.current.on('mouseover', () => {
          playerRef.current.play().then(() => {
            playerRef.current.muted(true);
          }).catch(error => console.error("Play error:", error));
        });

        playerRef.current.on('mouseout', () => {
          playerRef.current.pause();
          playerRef.current.currentTime(0);
          playerRef.current.posterImage.show();
        });
      });
    }
  };

  const getVideoMetadata = () => {
    if (!item.metadata) {
      return null;
    }

    if (FileService.hasVideoAnalytics(item.metadata)) {
      switch (item.metadata.external_video_provider) {
        case 'vimeo':
        case 'wistia':
        case 'vidyard':
          break;
        default:
          return VideoService.extractVideoProperties(item);
      }
    }

    return null;
  }

  const renderVideoPlayer = () => {
    return (
      <video
        ref={videoNode}
        preload="auto"
        muted
        loop
        poster={item.icon}
        className={styles.videoIcon}
      >
        <source src={video?.src} type={video?.type} />
      </video>
    );
  }

  const isNewAsset = () => {
    const currentTime = Date.now();
    const contentModifiedDate = Number(item.contentModifiedDate) || 0; 
    const lastUpdatedDuration = 14 * 24 * 60 * 60 * 1000;

    return (currentTime - contentModifiedDate) < (lastUpdatedDuration);
  }

  const queryParams = useMemo(() => {
    if (item && item.metadata) {
      const fileType = FileService.getFileType(item.metadata);
      switch (fileType) {
        case "PDF":
        case "PPT":
        case "PPTX":
        case "DOC":
        case "DOCX": {
          const page = item.readingMetrics ? item.readingMetrics.segment : 0;
          if (page) {
            return `?page=${page}`;
          }
          return "";
        }

        case 'VIDEO':
        case 'YOUTUBE':
        case 'VIMEO':
        case 'WISTIA':
        case 'VIDYARD':
          var segment = _.get(item,"readingMetrics.segment",0);
          if (segment > 0) {
            return `?segment=${segment}`;
          }
          return "";
        default:
          return "";
      }
    }
  }, [item]);

  const fileType = useMemo(() => {
    if (item != null) {
      return FileService.getFileType(item.metadata);
    } else {
      return;
    }
  }, [item]);

  const getProgress = (progress) => {
    switch (fileType) {
      case "PDF":
      case "PPT":
      case "PPTX":
      case "DOC":
      case "DOCX": {
        const documentProgress =
          Math.max(progress - 1, 0) / _.get(item, "metadata.pages");
        return documentProgress * 100;
      }
      case "VIDEO":
      case "YOUTUBE":
      case "VIMEO":
      case "WISTIA":
      case "VIDYARD": {
        const videoProgress =
          progress / (_.get(item, "metadata.video_duration") || progress);
        return videoProgress * 100;
      }
      case "AUDIO": {
        const audioProgress =
          progress / _.get(item, "metadata.audio_segment_length");
        return audioProgress * 100;
      }
      default:
        return 0;
    }
  };
  
  return (
    <div>
      <NavigateEntity className={styles.cursorPointer} path={`/assets/${item.id}${queryParams}`}
      onClick={() => {
        navigate(`/assets/${item.id}${queryParams}`);
      }}
      >
        <div className={styles.assetIcon}>
          {renderThumbnail(item.icon)}
          <div className={styles.fileType}>{fileType}</div>

          {video && (
            <div className={styles.playIcon}>
              <i className="paperflite-play"></i>
            </div>
          )}

          {video && (renderVideoPlayer())}

          {isNewAsset() && (<Badge className={styles.badge} />)}

          {internal &&
          <div className={styles.indicators}>
            <Tooltip content={'Internal use only'} placement='top'>
              <i className="paperflite-lock"></i>
            </Tooltip>
          </div>
          }
      
          <div
            className={cx(
              styles.progressBar,
              progress != null && styles.showProgressBar,
            )}
          >
            {progress != null && (
              <div
                className={cx(styles.progressLine)}
                style={{ width: `${getProgress(progress)}%` }}
              ></div>
            )}
          </div>
        </div>
        <Text className={styles.assetName} text={_.get(item, "name")} />
        <p className={styles.assetMetadata}>
          Updated on {dateFormat(item.modifiedDate, "dd mmm yyyy")}
        </p>
      </NavigateEntity>
    </div>
  );
}

export function PlaceholderAssetCard() {
  return (
    <div className={cx(styles.assetIcon, styles.placeholder)}>
      <div className={cx(styles.icon, styles.block)}></div>
      <div className={styles.cardContent}>
        <div className={styles.details}>
          <h3 className={cx(styles.assetName, styles.block)}></h3>
          <p className={cx(styles.assetMetadata, styles.block)}></p>
        </div>
      </div>
    </div>
  );
}
